import { FC, useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import useSignupContext from '@hooks/context/useSignupContext';
import { routes } from '@const/routes';
import { useFormContext, useWatch } from 'react-hook-form';
import FormField from '@components/form_field';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiRegistrationService } from '@api';
import emailIllustration from '@svg/illustrations/auth/email.svg';
import codeIllustration from '@svg/illustrations/auth/code.svg';
import Checkbox from '@components/common/checkbox';
import VerificationCode from '@components/verification_code';
import { EUserType } from '@xeppt/xeppt-sdk/types';

interface IProps {
    isEmailSent: boolean;
    toggleIsEmailSent: (val: boolean) => void;
}

const EmailSection: FC<IProps> = ({ isEmailSent, toggleIsEmailSent }) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'signup.email'
    });
    const { labelLocale, submitLocale, requestErrorLocale, placeholderLocale } = useLocales();
    const { nextStep, prevStep, handleChangeIllustration } = useSignupContext();
    const { trigger, getValues, control, setValue } = useFormContext();
    const [isAgree, setIsAgree] = useState(false);
    const [isAgreeMarketing, setIsAgreeMarketing] = useState(false);
    const [isErrorAgree, setIsErrorAgree] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);
    const [intervalId, setIntervalId] = useState<any>();
    const type = useWatch({ name: 'type', control });

    useEffect(() => {
        if (isEmailSent) {
            handleChangeIllustration(codeIllustration);
        } else {
            handleChangeIllustration(emailIllustration);
        }
    }, []);

    const { handleRequest: handleValidateEmail, isLoading: isValidationLoading } = useApiMutation({
        method: () => {
            const values = getValues();
            return apiRegistrationService.validateEmail(values.email);
        },
        onSuccess: () => {
            toggleIsEmailSent(true);
            setResendTimer(60);
            setIntervalId(
                setInterval(() => {
                    setResendTimer((state) => state - 1);
                }, 1000)
            );
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleVerifyEmail } = useApiMutation({
        method: () => {
            const values = getValues();
            return apiRegistrationService.verifyEmail(values.email, values.code);
        },
        onSuccess: nextStep,
        onError: requestErrorLocale
    });

    const onSubmit = () => {
        trigger().then((isValid) => {
            if (isValid) {
                if (isEmailSent) {
                    handleVerifyEmail(undefined);
                } else {
                    if (isAgree) {
                        handleValidateEmail(undefined);
                    } else {
                        setIsErrorAgree(true);
                    }
                }
            }
        });
        if (!isAgree) {
            setIsErrorAgree(true);
        }
    };

    const onResend = () => {
        handleValidateEmail(undefined);
        setValue('code', '');
    };

    useEffect(() => {
        if (resendTimer === 0) {
            clearInterval(intervalId);
        }
    }, [resendTimer]);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.steps}>
                STEP {isEmailSent ? '2' : '1'}/{type === EUserType.BUSINESS ? '11' : '7'}
            </div>
            <Typography className={styles.title} variant="h3">
                {t('title')}
            </Typography>
            {isEmailSent && (
                <Typography className={styles.email_sent_desc} variant="body2">
                    {t('enter_email_confirmation')} <span>{getValues().email}</span>
                </Typography>
            )}
            <div className={styles.form}>
                {!isEmailSent && (
                    <>
                        <FormField
                            name="email"
                            renderComponent={(props) => (
                                <Input
                                    {...props}
                                    full
                                    placeholder={placeholderLocale('enter_email')}
                                    readOnly={isEmailSent}
                                    required
                                    label={labelLocale('email')}
                                />
                            )}
                        />
                        <div
                            className={styles.agree}
                            onClick={() => {
                                setIsAgree((state) => !state);
                                setIsErrorAgree(false);
                            }}>
                            <Checkbox
                                checked={isAgree}
                                onClick={() => {
                                    setIsAgree((state) => !state);
                                    setIsErrorAgree(false);
                                }}
                                size="small"
                                variant={isErrorAgree ? 'red' : 'dark'}
                                className={styles.checkbox}
                            />
                            <Typography>
                                {t('by_registration')}{' '}
                                <Button
                                    variant="text-gold"
                                    size="small"
                                    onClick={() => {
                                        window.open(routes.terms_of_service, '_blank');
                                    }}>
                                    {t('term')}
                                </Button>{' '}
                                {t('and')}{' '}
                                <Button
                                    variant="text-gold"
                                    size="small"
                                    onClick={() => {
                                        window.open(routes.privacy_policy, '_blank');
                                    }}>
                                    {t('privacy')}
                                </Button>
                            </Typography>
                        </div>
                        <div
                            className={styles.marketing}
                            onClick={() => {
                                setIsAgreeMarketing((state) => !state);
                            }}>
                            <Checkbox
                                checked={isAgreeMarketing}
                                onClick={() => {
                                    setIsAgreeMarketing((state) => !state);
                                }}
                                size="small"
                                variant="dark"
                                className={styles.checkbox}
                            />
                            <Typography>
                                I agree to receive emails and notifications from XEPPT, including
                                product updates, offers, and news. I understand I can unsubscribe at
                                any time
                            </Typography>
                        </div>
                    </>
                )}
                {isEmailSent && (
                    <>
                        <FormField
                            name="code"
                            renderComponent={(props) => (
                                <VerificationCode {...props} onSubmit={onSubmit} />
                            )}
                        />
                        {resendTimer > 0 ? (
                            <p className={styles.resend_text}>
                                Resend the code in {resendTimer} sec{' '}
                            </p>
                        ) : (
                            <button className={styles.resend_button} onClick={onResend}>
                                {submitLocale('resend_code')}
                            </button>
                        )}
                    </>
                )}
                <div className={styles.actions}>
                    <Button
                        className={styles.back}
                        variant="outlined-primary"
                        leftIcon="arrow_left"
                        onClick={() => {
                            if (isEmailSent) {
                                toggleIsEmailSent(false);
                            } else {
                                prevStep();
                            }
                        }}>
                        {submitLocale('back')}
                    </Button>
                    <Button
                        onClick={onSubmit}
                        disabled={!isAgree}
                        variant="primary"
                        rightIcon={!isEmailSent ? 'arrow_right' : undefined}
                        isLoading={isValidationLoading}>
                        {submitLocale(isEmailSent ? 'verify' : 'next')}
                    </Button>
                </div>
            </div>
        </motion.div>
    );
};

export default EmailSection;
